/**
 * Design System Yoco - Theme MUI
 * Style: Clean, neutre, professionnel
 */

import { createTheme } from '@mui/material/styles';
import { grey, primary, success, warning, error, info, colors } from './tokens';
import { fontFamily, fontSize, fontSizeMobile, fontWeight, lineHeight } from './tokens';
import { mobile } from './tokens';

// Creer un theme de base pour acceder aux breakpoints
const baseTheme = createTheme();

const theme = createTheme({
  // ==================== PALETTE ====================
  palette: {
    primary: {
      main: primary[500],
      light: primary[400],
      dark: primary[600],
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: grey[700],
      light: grey[500],
      dark: grey[800],
    },
    background: {
      default: colors.bgPage,
      paper: colors.bgSurface,
    },
    text: {
      primary: colors.textPrimary,
      secondary: colors.textSecondary,
      disabled: colors.textDisabled,
    },
    divider: colors.border,
    success: {
      main: success.main,
      light: success[50],
      dark: success.dark,
    },
    warning: {
      main: warning.main,
      light: warning[50],
      dark: warning.dark,
    },
    error: {
      main: error.main,
      light: error[50],
      dark: error.dark,
    },
    info: {
      main: info.main,
      light: info[50],
      dark: info.dark,
    },
    grey: grey,
  },

  // ==================== TYPOGRAPHIE ====================
  typography: {
    fontFamily: fontFamily.body,
    // Page title - Mobile: plus grand
    h1: {
      fontFamily: fontFamily.heading,
      fontSize: fontSize['2xl'],  // 20px desktop
      fontWeight: fontWeight.semibold,
      lineHeight: lineHeight.tight,
      color: colors.textPrimary,
      [baseTheme.breakpoints.down('sm')]: {
        fontSize: fontSizeMobile['2xl'],  // 24px mobile
      },
    },
    // Section title
    h2: {
      fontFamily: fontFamily.heading,
      fontSize: fontSize.lg,  // 16px desktop
      fontWeight: fontWeight.semibold,
      lineHeight: lineHeight.tight,
      color: colors.textPrimary,
      [baseTheme.breakpoints.down('sm')]: {
        fontSize: fontSizeMobile.lg,  // 18px mobile
      },
    },
    // Card title
    h3: {
      fontFamily: fontFamily.heading,
      fontSize: fontSize.md,  // 14px desktop
      fontWeight: fontWeight.semibold,
      lineHeight: lineHeight.normal,
      color: colors.textPrimary,
      [baseTheme.breakpoints.down('sm')]: {
        fontSize: fontSizeMobile.md,  // 16px mobile
      },
    },
    h4: {
      fontFamily: fontFamily.heading,
      fontSize: fontSize.base,  // 13px desktop
      fontWeight: fontWeight.semibold,
      lineHeight: lineHeight.normal,
      color: colors.textPrimary,
      [baseTheme.breakpoints.down('sm')]: {
        fontSize: fontSizeMobile.base,  // 14px mobile
      },
    },
    h5: {
      fontFamily: fontFamily.heading,
      fontSize: fontSize.base,
      fontWeight: fontWeight.medium,
      lineHeight: lineHeight.normal,
      color: colors.textPrimary,
    },
    h6: {
      fontFamily: fontFamily.heading,
      fontSize: fontSize.sm,
      fontWeight: fontWeight.medium,
      lineHeight: lineHeight.normal,
      color: colors.textPrimary,
    },
    // Body text - Mobile: plus grand pour lisibilite
    body1: {
      fontSize: fontSize.base,  // 13px desktop
      fontWeight: fontWeight.regular,
      lineHeight: lineHeight.normal,
      color: colors.textPrimary,
      [baseTheme.breakpoints.down('sm')]: {
        fontSize: fontSizeMobile.base,  // 14px mobile
      },
    },
    body2: {
      fontSize: fontSize.sm,  // 12px desktop
      fontWeight: fontWeight.regular,
      lineHeight: lineHeight.normal,
      color: colors.textSecondary,
      [baseTheme.breakpoints.down('sm')]: {
        fontSize: fontSizeMobile.sm,  // 13px mobile
      },
    },
    // Small text
    caption: {
      fontSize: fontSize.sm,  // 12px
      fontWeight: fontWeight.regular,
      lineHeight: lineHeight.normal,
      color: colors.textSecondary,
    },
    // Labels
    overline: {
      fontSize: fontSize.xs,  // 11px
      fontWeight: fontWeight.semibold,
      lineHeight: lineHeight.normal,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: colors.textTertiary,
    },
    // Buttons
    button: {
      fontSize: fontSize.base,  // 13px
      fontWeight: fontWeight.medium,
      lineHeight: lineHeight.normal,
      textTransform: 'none',
    },
  },

  // ==================== SHAPE ====================
  shape: {
    borderRadius: 6,
  },

  // ==================== SHADOWS ====================
  shadows: [
    'none',
    '0 1px 2px rgba(0,0,0,0.04)',
    '0 1px 3px rgba(0,0,0,0.05)',
    '0 2px 4px rgba(0,0,0,0.05)',
    '0 2px 6px rgba(0,0,0,0.06)',
    '0 3px 8px rgba(0,0,0,0.06)',
    '0 4px 10px rgba(0,0,0,0.07)',
    '0 5px 12px rgba(0,0,0,0.08)',
    '0 6px 14px rgba(0,0,0,0.08)',
    '0 8px 16px rgba(0,0,0,0.09)',
    '0 10px 20px rgba(0,0,0,0.1)',
    ...Array(14).fill('0 10px 20px rgba(0,0,0,0.1)'),
  ],

  // ==================== COMPOSANTS ====================
  components: {
    // ===== CSS BASELINE =====
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colors.bgPage,
          color: colors.textPrimary,
        },
      },
    },

    // ===== BUTTONS =====
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'uppercase',
          fontWeight: fontWeight.medium,
          fontSize: fontSize.sm,  // 12px - reduit
          letterSpacing: '0.02em',
          borderRadius: 6,
          padding: '8px 16px',
          minHeight: 36,
          transition: 'all 0.15s ease',
          // Mobile: touch-friendly
          [baseTheme.breakpoints.down('sm')]: {
            minHeight: mobile.minTouchTarget,
            fontSize: fontSizeMobile.base,
            padding: '10px 16px',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          backgroundColor: primary[500],
          color: grey[900],  // Texte noir sur primary
          '&:hover': {
            backgroundColor: primary[600],
          },
          '&:active': {
            backgroundColor: primary[700],
          },
        },
        containedSecondary: {
          backgroundColor: grey[100],
          color: grey[900],  // Texte noir sur secondary
          '&:hover': {
            backgroundColor: grey[200],
          },
          '&:active': {
            backgroundColor: grey[300],
          },
        },
        outlined: {
          borderColor: colors.border,
          color: colors.textPrimary,
          '&:hover': {
            borderColor: colors.borderStrong,
            backgroundColor: colors.bgHover,
          },
        },
        text: {
          color: colors.textSecondary,
          '&:hover': {
            backgroundColor: colors.bgHover,
          },
        },
        sizeSmall: {
          padding: '6px 12px',
          fontSize: fontSize.sm,
          minHeight: 32,
        },
        sizeLarge: {
          padding: '10px 20px',
          fontSize: fontSize.md,
          minHeight: 40,
        },
      },
    },

    // ===== ICON BUTTON =====
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          color: grey[500],
          transition: 'all 0.15s ease',
          '&:hover': {
            backgroundColor: colors.bgHover,
            color: grey[700],
          },
          // Mobile: touch-friendly
          [baseTheme.breakpoints.down('sm')]: {
            minWidth: mobile.minTouchTarget,
            minHeight: mobile.minTouchTarget,
          },
        },
        sizeSmall: {
          padding: 6,
          [baseTheme.breakpoints.down('sm')]: {
            padding: 10,
          },
        },
        sizeMedium: {
          padding: 8,
          [baseTheme.breakpoints.down('sm')]: {
            padding: 10,
          },
        },
      },
    },

    // ===== TEXT FIELDS =====
    MuiTextField: {
      defaultProps: {
        size: 'small',
        variant: 'outlined',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontSize: fontSize.base,
          backgroundColor: colors.inputBg,
          transition: 'all 0.15s ease',
          '& fieldset': {
            borderColor: colors.inputBorder,
            transition: 'all 0.15s ease',
          },
          '&:hover fieldset': {
            borderColor: colors.inputBorderHover,
          },
          '&.Mui-focused fieldset': {
            borderColor: colors.inputBorderFocus,
            borderWidth: '1px',
          },
          // Mobile: larger text
          [baseTheme.breakpoints.down('sm')]: {
            fontSize: fontSizeMobile.base,
          },
        },
        input: {
          padding: '10px 12px',
          '&::placeholder': {
            color: colors.inputPlaceholder,
            opacity: 1,
          },
          // Mobile: larger touch target
          [baseTheme.breakpoints.down('sm')]: {
            padding: '12px 14px',
            fontSize: fontSizeMobile.base,
          },
        },
        inputSizeSmall: {
          padding: '8px 12px',
          [baseTheme.breakpoints.down('sm')]: {
            padding: '10px 12px',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: fontSize.base,
          color: colors.textSecondary,
          '&.Mui-focused': {
            color: colors.inputBorderFocus,
          },
        },
        sizeSmall: {
          fontSize: fontSize.base,
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          color: grey[400],
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          fontSize: fontSize.base,
        },
      },
    },

    // ===== CARDS =====
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: `1px solid ${colors.cardBorder}`,
          backgroundColor: colors.cardBg,
          transition: 'all 0.15s ease',
          // Mobile: full width, reduced radius
          [baseTheme.breakpoints.down('sm')]: {
            borderRadius: 6,
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 20,
          '&:last-child': {
            paddingBottom: 20,
          },
          // Mobile: reduced padding
          [baseTheme.breakpoints.down('sm')]: {
            padding: mobile.cardPadding,
            '&:last-child': {
              paddingBottom: mobile.cardPadding,
            },
          },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: '16px 20px',
        },
        title: {
          fontSize: fontSize.md,
          fontWeight: fontWeight.semibold,
        },
        subheader: {
          fontSize: fontSize.sm,
          color: colors.textSecondary,
        },
      },
    },

    // ===== PAPER =====
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        outlined: {
          border: `1px solid ${colors.border}`,
        },
      },
    },

    // ===== CHIPS =====
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: fontWeight.medium,
          fontSize: fontSize.sm,
          height: 24,
          borderRadius: 4,
        },
        sizeSmall: {
          height: 20,
          fontSize: fontSize.xs,
        },
        filled: {
          backgroundColor: grey[100],
          color: grey[700],
          '&:hover': {
            backgroundColor: grey[200],
          },
        },
        outlined: {
          borderColor: colors.border,
          backgroundColor: 'transparent',
        },
        colorPrimary: {
          backgroundColor: primary[50],
          color: primary[600],
        },
        colorSuccess: {
          backgroundColor: success[50],
          color: success.dark,
        },
        colorWarning: {
          backgroundColor: warning[50],
          color: warning.dark,
        },
        colorError: {
          backgroundColor: error[50],
          color: error.dark,
        },
        colorInfo: {
          backgroundColor: info[50],
          color: info.dark,
        },
      },
    },

    // ===== TABLES =====
    MuiTableContainer: {
      styleOverrides: {
        root: {
          // Pas de border ni radius ici - la Card parente s'en charge
          borderRadius: 0,
          overflow: 'hidden',
          // Mobile: scroll horizontal pour tables larges
          [baseTheme.breakpoints.down('sm')]: {
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            // Indicateur de scroll
            '&::-webkit-scrollbar': {
              height: 4,
            },
          },
        },
      },
    },
    // NOTE: MuiTable - pas de minWidth sur mobile, gere par MobileList ou scroll horizontal naturel
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: fontWeight.semibold,
            fontSize: fontSize.xs,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: colors.tableHeaderText,
            backgroundColor: colors.tableHeaderBg,
            borderBottom: `1px solid ${colors.tableBorder}`,
            borderRadius: 0,
            padding: '12px 16px',
            whiteSpace: 'nowrap',
            // Mobile: padding reduit
            [baseTheme.breakpoints.down('sm')]: {
              padding: '10px 12px',
              fontSize: fontSize.xs,
            },
            // Pas de border-radius sur les cellules header
            '&:first-of-type': {
              borderTopLeftRadius: 0,
            },
            '&:last-of-type': {
              borderTopRightRadius: 0,
            },
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root:last-child .MuiTableCell-root': {
            borderBottom: 'none',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.15s ease',
          '&:hover': {
            backgroundColor: colors.tableRowHover,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${colors.tableBorder}`,
          padding: '14px 16px',
          fontSize: fontSize.base,
          // Mobile: padding reduit, font plus lisible
          [baseTheme.breakpoints.down('sm')]: {
            padding: '12px 12px',
            fontSize: fontSizeMobile.base,
          },
        },
      },
    },

    // ===== DRAWER (Sidebar) =====
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.sidebarBg,
          borderRight: `1px solid ${colors.sidebarBorder}`,
        },
      },
    },

    // ===== LIST ITEMS =====
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          marginBottom: 2,
          paddingTop: 10,
          paddingBottom: 10,
          transition: 'all 0.15s ease',
          '&:hover': {
            backgroundColor: colors.sidebarHover,
          },
          '&.Mui-selected': {
            backgroundColor: colors.sidebarActive,
            '&:hover': {
              backgroundColor: colors.sidebarActive,
            },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 36,
          color: colors.sidebarIconDefault,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: fontSize.base,
          fontWeight: fontWeight.medium,
        },
      },
    },

    // ===== DIALOGS =====
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: fontSize.lg,
          fontWeight: fontWeight.semibold,
          padding: '20px 24px 12px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '12px 24px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '12px 24px 20px',
          gap: 8,
        },
      },
    },

    // ===== ALERTS =====
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontSize: fontSize.base,
          padding: '12px 16px',
          border: '1px solid',
        },
        icon: {
          opacity: 1,
        },
        standardSuccess: {
          backgroundColor: '#F0FDF4 !important',
          borderColor: '#DCFCE7 !important',
          color: '#16A34A !important',
          '& .MuiAlert-icon': {
            color: '#22C55E !important',
          },
        },
        standardError: {
          backgroundColor: '#FEF2F2 !important',
          borderColor: '#FEE2E2 !important',
          color: '#DC2626 !important',
          '& .MuiAlert-icon': {
            color: '#EF4444 !important',
          },
        },
        standardWarning: {
          backgroundColor: '#FFFBEB !important',
          borderColor: '#FEF3C7 !important',
          color: '#D97706 !important',
          '& .MuiAlert-icon': {
            color: '#F59E0B !important',
          },
        },
        standardInfo: {
          backgroundColor: '#EFF6FF !important',
          borderColor: '#DBEAFE !important',
          color: '#2563EB !important',
          '& .MuiAlert-icon': {
            color: '#3B82F6 !important',
          },
        },
      },
    },

    // ===== DIVIDER =====
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: colors.border,
        },
      },
    },

    // ===== MENU =====
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          border: `1px solid ${colors.border}`,
          marginTop: 4,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: fontSize.base,
          padding: '10px 16px',
          transition: 'background-color 0.15s ease',
          '&:hover': {
            backgroundColor: colors.bgHover,
          },
        },
      },
    },

    // ===== TABS =====
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 2,
          backgroundColor: primary[500],
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: fontWeight.medium,
          fontSize: fontSize.base,
          minHeight: 44,
          padding: '12px 16px',
          color: colors.textSecondary,
          '&.Mui-selected': {
            color: primary[500],
          },
        },
      },
    },

    // ===== TOOLTIP =====
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: grey[800],
          color: '#FFFFFF',
          fontSize: fontSize.sm,
          borderRadius: 4,
          padding: '6px 10px',
        },
      },
    },

    // ===== AVATAR =====
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontSize: fontSize.base,
          fontWeight: fontWeight.medium,
          backgroundColor: grey[200],
          color: grey[600],
        },
      },
    },

    // ===== SKELETON =====
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: grey[100],
        },
      },
    },

    // ===== CIRCULAR PROGRESS =====
    MuiCircularProgress: {
      defaultProps: {
        size: 24,
      },
    },

    // ===== FORM CONTROL =====
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiFormLabel-root': {
            fontSize: fontSize.base,
          },
        },
      },
    },

    // ===== GRID - Responsive defaults =====
    // NOTE: Ne pas modifier les margins/width du Grid - cela cause des debordements
    // Le spacing est gere par les props du composant Grid

    // ===== STACK - Mobile spacing =====
    MuiStack: {
      styleOverrides: {
        root: {
          // Responsive gaps handled by components
        },
      },
    },

    // ===== BOTTOM NAVIGATION =====
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          height: mobile.bottomNavHeight,
          backgroundColor: colors.bgSurface,
          borderTop: `1px solid ${colors.border}`,
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          color: grey[500],
          minWidth: 'auto',
          padding: '6px 12px 8px',
          '&.Mui-selected': {
            color: colors.brand,
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: fontSize.xs,
            marginTop: 2,
            '&.Mui-selected': {
              fontSize: fontSize.xs,
            },
          },
        },
      },
    },
  },
});

export default theme;
